import type {IntlShape} from 'react-intl';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The day the AI allowance resets, from the gateway's `period_end` (YYYY-MM-DD). Read as a local
 * calendar date: read as a UTC instant it names the previous day anywhere west of UTC. Null for
 * anything that is not a real date.
 */
export const creditsResetDate = (resetsOn: string | null | undefined): Date | null => {
	const match = resetsOn ? ISO_DATE.exec(resetsOn) : null;
	if (!match) return null;
	const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
	const date = new Date(year, month - 1, day);
	return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

/** The reason every disabled Nova control gives while the allowance is spent. */
export const formatCreditsReason = (
	intl: Pick<IntlShape, 'formatMessage' | 'formatDate'>,
	resetsOn: string | null | undefined
): string => {
	const date = creditsResetDate(resetsOn);
	if (!date) return intl.formatMessage({id: 'nova_invocation.control.reason_out_of_credits_no_date'});
	return intl.formatMessage(
		{id: 'nova_invocation.control.reason_out_of_credits'},
		{date: intl.formatDate(date, {day: 'numeric', month: 'long', year: 'numeric'})}
	);
};

/**
 * The long form, for the entry banner, which has room to explain rather than only refuse. It says
 * what is spent, when it comes back, that the rest of Forecast is unaffected, and who to ask for
 * more. Without a date it leaves the reset out rather than printing a sentence with a hole in it,
 * the same way the short form does.
 *
 * Beside formatCreditsReason rather than inside either caller, so the short and long forms read
 * the same date the same way - the short one on a row, this one on a banner, both from the
 * gateway's own period end.
 */
export const formatCreditsLong = (
	intl: Pick<IntlShape, 'formatMessage' | 'formatDate'>,
	resetsOn: string | null | undefined
): string => {
	const date = creditsResetDate(resetsOn);
	if (!date) return intl.formatMessage({id: 'nova_invocation.out_of_credits_no_date'});
	return intl.formatMessage(
		{id: 'nova_invocation.out_of_credits'},
		{resetDate: intl.formatDate(date, {day: 'numeric', month: 'long', year: 'numeric'})}
	);
};
